import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent, Button, useToast } from "@repo/ui";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { AlertCard } from "@/components/alerts/alert-card";
import { useAlertsContext } from "@/context/alerts-context";

export const Route = createFileRoute("/_app/alerts")({
  component: AlertsPage,
});

function AlertsPage() {
  const { alerts, counts, dismiss, dismissAll, isLoading } = useAlertsContext();
  const { toast } = useToast();

  const handleDismiss = async (alertKey: string) => {
    await dismiss(alertKey);
    toast({ title: "Alert dismissed", variant: "info" });
  };

  const handleDismissAll = async () => {
    await dismissAll();
    toast({ title: "All alerts dismissed", variant: "info" });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold text-foreground">Alerts</h1>
          {counts.total > 0 && (
            <span className="rounded-full bg-destructive px-2 py-0.5 text-xs font-medium text-white">
              {counts.total}
            </span>
          )}
        </div>
        {alerts.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDismissAll}
            data-testid="alerts-dismiss-all"
          >
            Dismiss All
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : alerts.length === 0 ? (
        <Card data-testid="alerts-empty-state">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle2 className="mb-3 h-10 w-10 text-green-500" />
            <p className="text-sm font-medium text-foreground">All clear!</p>
            <p className="text-xs text-muted-foreground">No active alerts</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="alerts-feed">
          {alerts.map((alert) => (
            <AlertCard
              key={alert.alertKey}
              id={alert.id}
              alertKey={alert.alertKey}
              type={alert.type}
              category={alert.category}
              severity={alert.severity}
              title={alert.title}
              message={alert.message}
              itemName={alert.itemName}
              ndc={alert.ndc}
              timestamp={alert.timestamp}
              actionUrl={alert.actionUrl}
              actionLabel={alert.actionLabel}
              onDismiss={handleDismiss}
            />
          ))}
        </div>
      )}
    </div>
  );
}
